import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, Sparkles } from "lucide-react";
import { getPaymentStatus } from "@/lib/payments";
import { generateProject } from "@/lib/api";

// Convert base64 stored in localStorage back to a File object
const b64ToFile = ({ name, type, data }) => {
  const bin = atob(data);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new File([arr], name, { type });
};

const PROGRESS_STEPS = [
  "Extraction des documents",
  "Analyse des lots (corps d'état)",
  "Calcul des précédences",
  "Optimisation du chemin critique",
];

export default function PaymentSuccess() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const sessionId = sp.get("session_id");
  const [phase, setPhase] = useState("verifying"); // verifying | generating | done | error
  const [genStep, setGenStep] = useState(0);
  const [error, setError] = useState("");
  const startedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setPhase("error");
      setError("Session de paiement manquante");
      return;
    }
    if (startedRef.current) return;
    startedRef.current = true;

    let mounted = true;
    let polls = 0;

    const generate = async () => {
      setPhase("generating");
      const stored = localStorage.getItem(`pending_project_${sessionId}`);
      if (!stored) {
        setPhase("error");
        setError("Données du projet introuvables. Merci de recommencer depuis Nouveau planning.");
        return;
      }
      const { payload, filesB64 } = JSON.parse(stored);
      const files = (filesB64 || []).map(b64ToFile);
      const timers = [
        setTimeout(() => setGenStep(1), 3000),
        setTimeout(() => setGenStep(2), 8000),
        setTimeout(() => setGenStep(3), 15000),
      ];
      try {
        const result = await generateProject(payload, files, sessionId);
        timers.forEach(clearTimeout);
        localStorage.removeItem(`pending_project_${sessionId}`);
        localStorage.removeItem("last_pending_session");
        if (!mounted) return;
        setPhase("done");
        setTimeout(() => nav(`/projet/${result.id}`), 800);
      } catch (e) {
        timers.forEach(clearTimeout);
        if (!mounted) return;
        setPhase("error");
        setError(e?.response?.data?.detail || "Erreur lors de la génération. Réessayez.");
      }
    };

    const check = async () => {
      polls += 1;
      try {
        const r = await getPaymentStatus(sessionId);
        if (!mounted) return;
        if (r.payment_status === "paid") {
          await generate();
          return;
        }
        if (["failed", "expired"].includes(r.payment_status)) {
          setPhase("error");
          setError("Paiement refusé.");
          return;
        }
        if (polls >= 20) {
          setPhase("error");
          setError("La confirmation du paiement prend trop de temps.");
          return;
        }
        setTimeout(check, 2000);
      } catch {
        if (polls >= 3) {
          setPhase("error");
          setError("Erreur de vérification du paiement.");
          return;
        }
        setTimeout(check, 2000);
      }
    };
    check();

    return () => {
      mounted = false;
    };
  }, [sessionId, nav]);

  return (
    <div className="max-w-lg mx-auto px-4 py-20" data-testid="payment-success-page">
      {phase === "verifying" && (
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto text-[#0F2137]" />
          <h1 className="font-display text-2xl font-bold text-[#0F2137] mt-4">
            Vérification du paiement...
          </h1>
          <p className="text-slate-500 mt-2 text-sm">
            Ne fermez pas cette page. Cela prend quelques secondes.
          </p>
        </div>
      )}

      {phase === "generating" && (
        <div data-testid="generating-panel">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#16A34A]/10 text-[#16A34A] font-mono-tech text-[11px] uppercase tracking-wider">
              <CheckCircle2 className="w-3.5 h-3.5" /> Paiement validé
            </div>
            <h1 className="font-display text-3xl font-extrabold text-[#0F2137] mt-4">
              Génération de votre planning
            </h1>
            <p className="text-slate-600 mt-2">
              Notre IA analyse votre projet et vos documents...
            </p>
          </div>
          <div className="mt-8 rounded-xl border border-slate-200 bg-white p-5">
            {PROGRESS_STEPS.map((s, i) => {
              const active = genStep >= i;
              const current = genStep === i;
              return (
                <div key={i} className="flex items-center gap-3 py-2">
                  <div
                    className={`w-2.5 h-2.5 rounded-full ${
                      active ? "bg-[#F59E0B]" : "bg-slate-300"
                    } ${current ? "animate-pulse" : ""}`}
                  />
                  <span
                    className={`text-sm ${
                      active ? "text-[#0F2137] font-medium" : "text-slate-400"
                    }`}
                  >
                    {s}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {phase === "done" && (
        <div className="text-center" data-testid="payment-paid">
          <CheckCircle2 className="w-16 h-16 mx-auto text-[#16A34A]" />
          <h1 className="font-display text-3xl font-extrabold text-[#0F2137] mt-4">
            Planning prêt !
          </h1>
          <p className="text-slate-600 mt-2">Ouverture de votre planning...</p>
        </div>
      )}

      {phase === "error" && (
        <div className="text-center" data-testid="payment-error">
          <XCircle className="w-16 h-16 mx-auto text-red-500" />
          <h1 className="font-display text-2xl font-bold text-[#0F2137] mt-4">
            Un problème est survenu
          </h1>
          <p className="text-slate-600 mt-2 text-sm">{error}</p>
          <div className="mt-6 flex gap-3 justify-center">
            <Link to="/nouveau">
              <Button className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0F2137] font-semibold">
                <Sparkles className="w-4 h-4 mr-1.5" /> Recommencer
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline">Accueil</Button>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
